import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import ListingTypeIcons from '@components/table/icons/ListingTypeIcons';
import StatusBadges from '@components/badges/StatusBadges';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import ListingActionButtons from '@components/table/actionButtons/ListingActionButtons';
import AgentAvatar from '@components/table/avatar/AgentAvatar';
import DataTable from '../dataTable';
import translateColumns, { translateSort } from '../dataTable/dataTranslate';

const ListingsTable = ({
  displayData,
  onListingSelect,
  onCopyClick,
  onAuditClick,
  isModal = false,
}) => {
  const actionsFormatter = (cell, row) => (
    <ListingActionButtons
      viewClicked={onListingSelect}
      copyClicked={onCopyClick}
      auditClicked={onAuditClick}
      currRow={row}
      isCopiable={row?.isCopyable}
      isEditable={row?.isEditable}
      isAuditable={row?.isAuditable}
      sizeSM={false}
    />
  );

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'actions',
      text: ' ',
      formatter: actionsFormatter,
      headerStyle: { width: '175px' },
    },
    {
      dataField: 'UniqueID',
      text: 'Unique ID',
      hidden: true,
      sort: false,
      searchable: false,
    },
    {
      dataField: 'ListingID',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '40px' },
    },
    {
      dataField: 'StandardStatusVerb',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '70px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'PropertyType',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '50px' },
      align: 'center',
      formatter: cellContent => (isEmpty(cellContent) ? '' : <ListingTypeIcons type={cellContent} />),
    },
    {
      dataField: 'UnparsedAddress',
      text: 'Address',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-175',
    },
    {
      dataField: 'City',
      text: 'City',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-150',
    },
    {
      dataField: 'ListAgentFullName',
      text: 'Listing Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '150px' },
      formatter: cellContent => (isEmpty(cellContent) ? (
        ''
      ) : (
        <AgentAvatar fullName={cellContent} showFullName />
      )),
    },
    {
      dataField: 'CoListAgentFullName',
      text: 'Co-List Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '150px' },
      formatter: cellContent => (isEmpty(cellContent) ? (
        ''
      ) : (
        <AgentAvatar fullName={cellContent} showFullName />
      )),
    },
  ];

  // Sets the default sort order to desc by ListingId
  const defaultSorted = [
    {
      dataField: 'ListingId',
      order: 'desc',
    },
  ];

  const newCols = useMemo(() => translateColumns(columns), [columns]);

  return (
    <DataTable
      defaultSorted={translateSort(defaultSorted)}
      columns={newCols}
      sizePerPage={isModal ? 5 : 10}
      data={displayData}
      wrapperClasses="table-responsive"
      id="listingTable"
      noDataIndication="Your search returned no results. Please try adjusting your search criteria."
      classes="dataTable"
      striped
      hover
      searchComponent={searchProps => (
        <TableSearchInputGroup {...searchProps} />
      )}
    />
  );
};

ListingsTable.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onListingSelect: PropTypes.func.isRequired,
  onCopyClick: PropTypes.func.isRequired,
  onAuditClick: PropTypes.func.isRequired,
  isModal: PropTypes.bool,
};

export default ListingsTable;
